import { useContext } from 'react';
import {
  Button,
  ButtonStyles,
  Theme,
  ButtonWidth,
  useTranslation,
  useSession,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import {
  type BundleSlot,
  CartEventsEnum,
  ErrorAPI,
  InitialCartListState,
  type ListEntry,
  PublishWishlistEvent,
  UserListResponse,
  myListApi,
  productEntryData,
  validateBundleInEntries,
} from '@commerce-cart-webcomponents/sdk';
import { useSelector } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import { CartNotificationTheme } from '../../common/components/cart-notification/types/enums';
import { useCartNotification } from '../../common/hooks/useCartNotification';
import { TOAST_TIMEOUT } from '../add-to-cart-button/types/enums';
import AllCustomListContext from '../../common/CustomListContext';
import { useExceptionToast } from '../../common';
import { AddToListButtonPropTypes } from './types/propsTypes';
import styles from './AddToListButton.module.scss';

export const AddToListButton = ({ disabled = false, className = '', entryObj, size }: AddToListButtonPropTypes) => {
  const t = useTranslation();
  const { createToast, deleteToast } = useCartNotification('addToCartButton');
  const addedToWishlist = t('cart.addToListToggle.ProductAddedToWishlist');
  const addedToList = t('cart.addToListToggle.ProductAddedToList');

  const { isRegistered } = useSession();
  const router = useNextgenRouter();

  const { customList, updateCustomList } = useContext(AllCustomListContext);
  const { myList }: InitialCartListState = useSelector((state: AppState) => state.commerceMyList);
  const currentList = router.params?.listId === myList?.listId ? myList : customList;
  const triggerToast = useExceptionToast();

  const addToList = () => {
    const { productId, bundleSlots, quantity = 1 } = entryObj;
    const entriesFromCart = validateBundleInEntries([
      {
        product: {
          id: productId,
        },
        quantity,
        bundleSlots: bundleSlots as BundleSlot[],
      },
    ]);
    myListApi
      .addToList([
        {
          version: currentList?.version as number,
          listId: currentList?.listId as string,
          entries: entriesFromCart as ListEntry[],
        },
      ])
      .then((res: UserListResponse) => {
        // we only need the list not the list array.
        updateCustomList(res?.lists?.[0] || {});
        const productEntry = productEntryData(res, productId);
        PublishWishlistEvent({
          cartEvent: CartEventsEnum.AddToWishlist,
          listEntry: productEntry,
        });
        createToast(
          {
            theme: CartNotificationTheme.SUCCESS,
            rightSeperator: false,
            content: isRegistered() ? addedToWishlist : addedToList,
            clickIcon: 'times',
            icon: 'check-circle',
            timeOut: TOAST_TIMEOUT,
          },
          deleteToast,
        );
      })
      .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps));
  };

  return (
    <>
      <div className={`${styles['add-to-list-container']} ${className}`}>
        <div className={styles['container']}>
          <Button
            id={'add-to-list-button'}
            onClick={addToList}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            title={t('cart.addToListButton.addToList')}
            buttonStyle={ButtonStyles.SOLID}
            theme={Theme.BLACK}
            size={size}
            tabIndex={0}
            disabled={disabled}
            aria-label="Add to List Button"
            role="button"
          />
        </div>
      </div>
    </>
  );
};

export type { AddToListButtonPropTypes };
